// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import { Component, type ComponentType } from 'react';

import { AsyncOverlay, Document, MessageBar } from '@zapier/common-components';
import { Border, Colors } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

import BillingOptions from './BillingOptions';
import ChangePlanButtons from './ChangePlanButtons';
import ChangePlanHeader from './ChangePlanHeader';
import ChangePlanIndicator from './ChangePlanIndicator';
import ChangePlanSection from './ChangePlanSection';
import ChangePlanSummary from './ChangePlanSummary';

import type { ChangeType, Interval, Plan } from '@zapier/billing/src/types';

/** @jsx jsx */

type Props = {
  accountId: number,
  accountName?: string,
  ChangePlanCompleteModal: ComponentType<*>,
  changeType: ChangeType,
  companyName?: string,
  enticements: { month: string, year: string },
  error: ?string,
  fromPlanName: string,
  heading: string,
  isBillingInFlight: boolean,
  isCompact: boolean,
  isConfirmingPlanChange: boolean,
  isCreatingNewAccount?: boolean,
  isNewAccount?: boolean,
  isSubmitDisabled: boolean,
  onCancelChangePlan: () => void,
  onChangePlan: () => void,
  onSelectBillingInterval: (planName: string, interval: Interval) => void,
  PaymentApp: ?ComponentType<*>,
  plan: Plan,
  selectedBillingInterval: string,
  subheading?: string,
  submitText: string,
};

const changePlanCss = (props: Props) =>
  css(
    {
      backgroundColor: Colors.white,
      border: `${Border.size.default} solid ${Border.color.default}`,
      borderRadius: '5px',
      margin: '2em auto',
      maxWidth: 800,
      padding: '2em',
      width: 'auto',
    },
    props.isCompact && {
      border: 'none',
    }
  );

const buttonsCss = {
  marginTop: '2em',
};

const errorCss = {
  marginTop: 18,
  textAlign: 'center',
};

class ChangePlan extends Component<Props> {
  render() {
    const { props } = this;
    const { ChangePlanCompleteModal, changeType, PaymentApp } = props;

    return (
      <Document title={props.heading}>
        {props.isConfirmingPlanChange && (
          <ChangePlanCompleteModal
            changeType={changeType}
            isCreatingNewAccount={props.isCreatingNewAccount}
            toPlanId={props.plan.id}
          />
        )}
        <AsyncOverlay
          isVisible={props.isBillingInFlight}
          renderAsyncIndicator={
            props.isBillingInFlight ? () => <ChangePlanIndicator /> : undefined
          }
        >
          <form css={changePlanCss(props)} onSubmit={e => e.preventDefault()}>
            {!props.isCompact && (
              <ChangePlanHeader
                accountName={props.accountName}
                companyName={props.companyName}
                heading={props.heading}
                subheading={props.subheading}
              />
            )}

            <BillingOptions
              enticements={props.enticements}
              hasMeteredBilling={Boolean(props.plan.hasMeteredBilling)}
              intervals={props.plan.intervals}
              onSelectBillingInterval={props.onSelectBillingInterval}
              planName={props.plan.name}
              selectedBillingInterval={props.selectedBillingInterval}
            />

            {PaymentApp && (
              <ChangePlanSection title="Payment Details">
                <PaymentApp editInModal={true} />
              </ChangePlanSection>
            )}

            <ChangePlanSection title="Summary">
              <ChangePlanSummary
                accountId={props.accountId}
                plan={props.plan}
              />
            </ChangePlanSection>

            <div css={buttonsCss}>
              <ChangePlanButtons
                changeType={props.changeType}
                fromPlanName={props.fromPlanName}
                isDisabled={props.isSubmitDisabled}
                onCancelChangePlan={props.onCancelChangePlan}
                onChangePlan={props.onChangePlan}
                planName={props.plan.name}
                selectedBillingInterval={props.selectedBillingInterval}
                submitText={props.submitText}
              />
            </div>
            {props.error && (
              <div css={errorCss}>
                <MessageBar type="error">{props.error}</MessageBar>
              </div>
            )}
          </form>
        </AsyncOverlay>
      </Document>
    );
  }
}

export default ChangePlan;
